import { useState } from 'react';
import { Globe, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';

export default function LanguageSettings() {
  const { t } = useTranslation();
  const { language, changeLanguage, availableLanguages } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSave = async () => {
    if (selectedLanguage === language) return;

    setSaving(true);
    setMessage(null);

    try {
      await changeLanguage(selectedLanguage);
      setMessage({ type: 'success', text: t('admin.languageSaved') });
    } catch (error) {
      setMessage({ type: 'error', text: t('admin.languageError') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800 mb-2">
          {t('admin.languageSettings')}
        </h2>
        <p className="text-slate-600 text-sm">
          {t('admin.selectLanguage')}
        </p>
      </div>

      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <div className="flex items-center gap-3 mb-2">
          <Globe className="w-5 h-5 text-slate-600" />
          <span className="font-medium text-slate-700">{t('admin.currentLanguage')}:</span>
          <span className="text-slate-900 font-semibold">
            {availableLanguages.find(l => l.code === language)?.nativeName}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {availableLanguages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => setSelectedLanguage(lang.code)}
            className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
              selectedLanguage === lang.code
                ? 'border-emerald-600 bg-emerald-50'
                : 'border-slate-200 hover:border-slate-300 bg-white'
            }`}
          >
            <div className="text-left">
              <p className={`font-semibold ${
                selectedLanguage === lang.code ? 'text-emerald-900' : 'text-slate-800'
              }`}>
                {lang.nativeName}
              </p>
              <p className="text-sm text-slate-600">{lang.name}</p>
            </div>
            {selectedLanguage === lang.code && (
              <Check className="w-6 h-6 text-emerald-600" />
            )}
          </button>
        ))}
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success'
            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving || selectedLanguage === language}
        className="w-full px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-semibold rounded-lg transition-colors"
      >
        {saving ? t('common.loading') : t('admin.saveLanguage')}
      </button>
    </div>
  );
}
